/**
 * ui/services/sync-instance.ts
 *
 * The app's account sync, over the device's IndexedDB stores.
 */
import { IndexedDBPuzzleProgress } from '../../adapters/indexed-db-puzzle-progress.js';
import { IndexedDBStorage } from '../../adapters/indexed-db-storage.js';
import { IndexedDBReplayStore } from '../../adapters/indexed-db-replays.js';
import { IndexedDBDraftStore } from '../../adapters/indexed-db-drafts.js';
import { AccountService } from './account.js';
import { ReplayService } from './replays.js';
import { DraftService } from './drafts.js';
import { SyncService } from './sync.js';
import { SYNC_APPLIED_EVENT, type SyncAppliedDetail } from './sync-events.js';

export { SYNC_APPLIED_EVENT, type SyncAppliedDetail } from './sync-events.js';

export const syncService = new SyncService(
  AccountService.instance,
  {
    progress: new IndexedDBPuzzleProgress(),
    sessions: new IndexedDBStorage(),
    replays: new IndexedDBReplayStore(),
    drafts: new IndexedDBDraftStore(),
  },
  {
    onApplied: (applied) => {
      if (applied.has('replay')) ReplayService.instance.notifyChanged();
      const detail: SyncAppliedDetail = Object.fromEntries([...applied].map(([kind, ids]) => [kind, [...ids]]));
      globalThis.dispatchEvent?.(new CustomEvent(SYNC_APPLIED_EVENT, { detail }));
    },
  },
);

/** Starts syncing in the background (a no-op while signed out). */
export function startSync(): () => void {
  const stop = syncService.start();
  // A new replay (a solve) goes up soon, not at the next interval.
  const stopReplays = ReplayService.instance.onChanged(() => syncService.syncSoon());
  // Editor autosaves are frequent; syncSoon batches them.
  const stopDrafts = DraftService.instance.onChanged(() => syncService.syncSoon());
  return () => {
    stop();
    stopReplays();
    stopDrafts();
  };
}
